import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { footerLinks } from "../../data/footerLinks";
import { useExternalRedirect } from "../../hooks/useExternalRedirect";
import { FooterSection } from "./FooterSection";
import { SocialLinks } from "./SocialLinks";

const Footer = () => {
  const { handleExternalRedirect } = useExternalRedirect();
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showEasterEgg, setShowEasterEgg] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [bottomScrollCount, setBottomScrollCount] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      
      setShowScrollTop(currentScrollY > 300);

      // Check if user is at the bottom of the page
      if (currentScrollY >= maxScroll - 10) {
        // User scrolled down to bottom
        if (currentScrollY > lastScrollY) {
          setBottomScrollCount(prev => {
            const newCount = prev + 1;
            if (newCount >= 5) {
              setShowEasterEgg(true);
            }
            return newCount;
          });
        }
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY, bottomScrollCount]);

  const handleFooterLinkClick = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Sort footerLinks by link count (ascending)
  const sortedFooterLinks = [...footerLinks].sort((a, b) => a.links.length - b.links.length);

  return (
    <footer className="bg-transparent py-12 mt-auto relative text-gray-800 dark:text-gray-200">
      <div className="absolute inset-0 bg-white bg-opacity-70 dark:bg-black dark:bg-opacity-70 backdrop-blur-sm -z-10"></div>
      <div className="container mx-auto px-1 sm:px-4">
        <div className="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-[repeat(auto-fit,minmax(160px,1fr))] sm:gap-8 mb-12">
          {sortedFooterLinks.map((section, index) => (
            <motion.div
              key={section.title}
              style={{ padding: "0 0.5rem" }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <FooterSection
                title={section.title}
                links={section.links.map((link) => ({
                  ...link,
                  onClick: handleFooterLinkClick,
                }))}
                handleExternalRedirect={handleExternalRedirect}
              />
            </motion.div>
          ))}
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <SocialLinks />
        </motion.div>
        <motion.div
          className="text-center mt-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
        >
          <p className="text-sm dark:text-white text-black">
            © {new Date().getFullYear()} Thrivebot. All rights reserved.
          </p>
          {showEasterEgg && (
            <motion.p 
              className="text-6xl mt-4 font-lacquer text-black dark:text-white cursor-pointer" 
              aria-label="brand-name"
              initial={{ opacity: 0, scale: 0.5, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ 
                type: "spring",
                stiffness: 200,
                damping: 15,
                duration: 0.6 
              }}
              style={{
                transformStyle: "preserve-3d",
                perspective: 1000,
              }}
            >
              {["t", "h", "r", "i", "v", "e", "b", "o", "t", ".", "i", "n"].map((char, index) => (
                <motion.span
                  key={index}
                  style={{ display: "inline-block" }}
                  initial={{ y: 50, opacity: 0, rotateZ: -180 }}
                  animate={{ 
                    y: 0, 
                    opacity: 1, 
                    rotateZ: 0,
                    scale: [1, 1.2, 1],
                  }}
                  transition={{
                    delay: index * 0.1,
                    duration: 0.6,
                    ease: "easeOut"
                  }}
                  whileHover={{
                    y: -20,
                    scale: 1.3,
                    rotateZ: [0, -10, 10, 0],
                    color: ["currentColor", "#3b82f6", "#8b5cf6", "currentColor"],
                  }}
                >
                  {char}
                </motion.span>
              ))}
            </motion.p>
          )}
        </motion.div>
      </div>
      {showScrollTop && (
        <motion.button
          className="fixed bottom-8 right-8 bg-primary p-3 rounded-full shadow-lg hover:bg-primary-dark transition-colors duration-300 z-50 text-black dark:text-white"
          onClick={handleScrollToTop}
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 10l7-7m0 0l7 7m-7-7v18"
            />
          </svg>
        </motion.button>
      )}
    </footer>
  );
};

export default Footer;